import * as React from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { Api } from '../../typings/api';
import { ListDisplayType } from '../../containers/DriveItemList/constants';
import Loader, { LoaderType, LoaderStepName, LoaderStep } from '../Loader';
import {
	ListItemCardInner, ListItemContainerVariants, ListItemCardVariants,
	ListItemComponentVariants, ListItemUnreadBadge
} from './styles';
import { ThumbnailDescriptor } from '../../utils/driveItem';
import ListItemThumbnail from './Thumbnail';
import ListItemInfos from './../../containers/ListItem/Infos';
import { DndItemTypes } from '../../typings/dnd';
import DropTarget from '../../containers/DriveItemList/DropTarget';
import FolderDropTarget from '../../containers/DriveItemList/FolderDropTarget';

interface ListItemProps {
	onClick: () => void;
	showUnreadBadge: boolean;
	displayType: ListDisplayType;
	driveItemType: Api.DriveItemType;
	hasActionButton: boolean;
	label: string;
	isSelectedForContextualMenu: boolean;
	isDownloading: boolean;
	isDownloaded: boolean;
	loaderStep: LoaderStep | undefined;
	thumbnailDescriptor: ThumbnailDescriptor;
	isUnread: boolean;
	isEditMode: boolean;
	driveItemId?: number;
	hasSelectionIcon?: boolean;
	isDraggable?: boolean;
	index?: number;
	driveItem?: Api.DriveItem;
	itemsCount?: number;
	isDragPreview?: boolean;
}

const ListItem = ({
	onClick,
	displayType,
	driveItemType,
	hasActionButton,
	label,
	isSelectedForContextualMenu,
	isDownloading,
	loaderStep,
	thumbnailDescriptor,
	isUnread,
	isEditMode,
	driveItemId,
	hasSelectionIcon = true,
	isDraggable,
	index = -1,
	driveItem,
	itemsCount = 0,
	isDragPreview,
}: ListItemProps) => {
	const [isActive, setIsActive] = React.useState(false);

	const [{ isDragging }, drag, preview] = useDrag({
		item: {
			type: DndItemTypes.DRIVE_ITEM,
			index,
			driveItem,
			displayType,
		},
		begin: () => {
			// using react state cause performance issues when there are many items in drive
			setTimeout(() => document.body.classList.add('is-dragging'), 0);
		},
		end: () => {
			document.body.classList.remove('is-dragging');
		},
		collect: monitor => ({
			isDragging: !!monitor.isDragging(),
		}),
	});

	React.useEffect(() => {
		preview(getEmptyImage(), { captureDraggingState: true });
	}, []);

	const handleContextualMenuHover = (e: MouseEvent, isActive: boolean) => {
		e.stopPropagation();
		setIsActive(isActive);
	};

	const onCardLeave = (e: MouseEvent) => {
		handleContextualMenuHover(e, false);
	};

	const onContextualMenuHover = (e: MouseEvent) => {
		handleContextualMenuHover(e, true);
	};

	const ListItemContainer = ListItemContainerVariants[displayType];
	const isActivated = isSelectedForContextualMenu || isActive;
	const ListItemCard = isActivated
		? ListItemCardVariants[displayType]
		: ListItemComponentVariants[displayType];

	return (
		<>
			<ListItemContainer
				isEditMode={isEditMode}
				itemsCount={itemsCount}
			>
				{isDraggable &&
					<DropTarget index={index} />
				}
				{isDraggable && index === itemsCount - 1 &&
					<DropTarget
						index={index + 1}
						isLast={true}
					/>
				}
				{isDraggable && driveItemType === Api.DriveItemType.Directory &&
					<FolderDropTarget folderId={driveItemId as number} />
				}
				{isUnread && displayType !== ListDisplayType.Coverflow &&
					<ListItemUnreadBadge />
				}
				<ListItemCard
					ref={isDraggable ? drag : null}
					isDraggable={isDraggable}
					isDragging={isDragging}
					isDragPreview={isDragPreview}
					onClick={isDraggable ? undefined : onClick}
					isSelectedForContextualMenu={isSelectedForContextualMenu}
					onMouseLeave={onCardLeave}
				>
					<ListItemCardInner isRowView={displayType === ListDisplayType.Row}>
						{isDownloading &&
							<Loader
								loaderStep={loaderStep ? loaderStep : { step: LoaderStepName.Downloading }}
								type={LoaderType.Overlay}
							/>
						}
						<ListItemThumbnail
							selectionId={driveItemId}
							displayType={displayType}
							thumbnailDescriptor={thumbnailDescriptor}
							isActivated={isActivated}
							hasSelectionIcon={hasSelectionIcon && displayType !== ListDisplayType.Coverflow}
						/>
						{displayType !== ListDisplayType.Coverflow &&
							<ListItemInfos
								driveItemId={driveItemId}
								hasActionButton={hasActionButton}
								label={label}
								onContextualMenuHover={onContextualMenuHover}
								isActiveIcon={isActivated}
								isRowView={displayType === ListDisplayType.Row}
							/>
						}
					</ListItemCardInner>
				</ListItemCard>
			</ListItemContainer>
		</>
	);
};

export default ListItem;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ListItem/index.tsx